import { css, html, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { KryptonElement } from './krypton-element';
import { KryptonEventDetail } from './krypton-event-detail';
import { MenuItem } from './menu-item';
import { Searchable } from './search-menu';


export class PageNav extends KryptonElement implements Searchable<MenuItem> {

    static styles = css`
      :host {
          position: fixed;
          top: 0;
          left: var(--krypton-base-nav-offset);
          min-height: 100vh;
          height: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          -ms-overflow-style: -ms-autohiding-scrollbar;
          transition: transform 0.5s ease-out;
          -webkit-transition: transform 0.5s ease-out;
          -moz-transition: transform 0.5s ease-out;
          -o-transition: transform 0.5s ease-out;
          transition: transform var(--nav-transition-timing) var(--nav-transition-function);
          -webkit-transition: transform var(--nav-transition-timing) var(--nav-transition-function);
          -moz-transition: transform var(--nav-transition-timing) var(--nav-transition-function);
          -o-transition: transform var(--nav-transition-timing) var(--nav-transition-function);
          width: var(--krypton-base-nav-width);
          background-color: var(--krypton-color-gray-mid);
          z-index: var(--z-index-krypton-base-nav);
          -webkit-overflow-scrolling: touch;
          padding-bottom: 5rem; /* padding for mobile usability */
          box-sizing: border-box;
          scrollbar-width: thin;
          scrollbar-color: rgba(85, 107, 123, 0.5);
      }

      :host::-webkit-scrollbar {
          width: 4px; /* remove scrollbar space */
          background: transparent; /* optional: just make scrollbar invisible */
      }

      /*optional: show position indicator in red*/
      :host::-webkit-scrollbar-thumb {
          background: #5b7283;
      }

      :host(.open) {
          transform: translateX(100%);
          -webkit-transform: translateX(100%);
          -moz-transform: translateX(100%);
          box-shadow: 1px 1px 4px #666666;
      }

      #main-nav {
          padding: 0;
          font-size: 1rem;
          font-weight: bold;
      }

      #header-mobile-nav {
          border-top: 1px solid #EEE;
          padding-top: 2rem;
      }

      #header-mobile-nav > li {
          list-style: none;
          padding-left: 1rem;
          margin: 0.75rem auto;
      }

      #header-mobile-nav > li > a {
          font-size: 1.3rem;
          line-height: 1.3rem;
      }

      /* ***********************
         * Responsive Styles
         */

      @media only screen and (min-width: 320px) {
          :host {
              width: 80vw;
              left: -80vw;
              overflow-y: scroll;
          }
      }

      @media only screen and (min-width: 768px) {
          :host {
              width: 50vw;
              left: -50vw;
              overflow-y: scroll;
          }
      }

      @media only screen and (min-width: 992px) {
          :host {
              width: var(--krypton-base-nav-width);
              left: var(--krypton-base-nav-offset);
              overflow-y: auto;
          }
      }
    `;

    render() {
        return html`
            <div>
                <search-menu id="nav-search" selector="[tag*='$0']"></search-menu>
            </div>
            <slot></slot>
            <div>
                <slot name="bottom"></slot>
            </div>
        `;
    }

    firstUpdated(changedProperties: PropertyValues) {
        this.addEventListener(KryptonEventDetail.MENU_ITEM_EVENT, ((e: CustomEvent) => {
            this.onMenuItemEvent(e);
        }) as EventListener);
    }

    _open: boolean = false;

    get open() {
        return this._open;
    }

    @property({ type: Boolean, reflect: true })
    set open(open) {
        const oldValue = this._open;
        this._open = open;
        if (open) {
            this.classList.add("open");
            this.fire(KryptonEventDetail.PAGE_NAV_EVENT, new KryptonEventDetail(KryptonEventDetail.NAV, KryptonEventDetail.PAGE_NAV_EVENT, { open: true }));
        } else {
            this.classList.remove("open");
            this.fire(KryptonEventDetail.PAGE_NAV_EVENT, new KryptonEventDetail(KryptonEventDetail.NAV, KryptonEventDetail.PAGE_NAV_EVENT, { open: false }));
            this.async(this.collapseAll.bind(this), 500, "collapseAll");
        }
        this.requestUpdate("open", oldValue);
    }

    expandAll() {
        this.menuItems.forEach(menuItem => menuItem.expandOptions());
    }

    collapseAll() {
        this.menuItems.forEach(menuItem => menuItem.collapseOptions());
    }

    onMenuItemEvent(e: CustomEvent) {
        if (e.detail instanceof KryptonEventDetail) {
            let kryptonEvent: KryptonEventDetail = e.detail;
            if (kryptonEvent.getType() === KryptonEventDetail.CLICK) {
                this.open = false;
            }
        }
    }

    //-- Searchable implementation

    flatten(flatten: boolean = true) {
        //this.querySelectorAll("menu-item").forEach(m => (m as MenuItem).flatten(flatten));
        this.menuItems.forEach(m => (m as MenuItem).flatten(flatten));
    }

    searchStart() {
        //--do nothing
    }

    searching(searching: boolean) {
        this.flatten(searching);
    }

    searchEnd() {
        // let menuItems: MenuItem[] = [].slice.call(this.children);
        const menuItems = this.menuItems;
        menuItems.shift();
        menuItems.forEach(mi => {
            if (mi.matches("menu-item.category.search-hit:not(.search-hit-parent)"))
                mi.style.setProperty("display", "none");
            else
                mi.style.removeProperty("display");
        });
    }

    searchEntries(): MenuItem[] {
        //return this.querySelectorAll("menu-item");
        const menuItems = this.menuItems;
        const searchEntries: MenuItem[] = [];
        menuItems.forEach(m => {
            searchEntries.push(m);
            m.querySelectorAll("menu-item").forEach(o => searchEntries.push(o))
        });
        return searchEntries;
    }

    get menuItems(): MenuItem[] {
        const slot = this.shadowRoot?.querySelector("slot:not([name])") as HTMLSlotElement;
        return this.getNestedSlotElements(slot, MenuItem) as MenuItem[];
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'page-nav': PageNav;
    }
}
